import { useState } from 'react';
import type { PlanRow, WeekKey } from '@td/shared';
import { useSaveRows, useSaveTemplate } from '../hooks/useLessonPlans';
import { Modal } from './Modal';

let tempId = 0;
const newId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `row-${++tempId}`;

/** Edits the week's schedule: which subjects, in what order, at what times. */
export function PlanRowEditor({
  week,
  rows,
  onClose,
}: {
  week: WeekKey;
  rows: PlanRow[];
  onClose: () => void;
}) {
  const saveRows = useSaveRows();
  const saveTemplate = useSaveTemplate();
  const [draft, setDraft] = useState<PlanRow[]>(rows);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  function update(id: string, patch: Partial<PlanRow>) {
    setDraft((d) => d.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function move(index: number, dir: -1 | 1) {
    const next = index + dir;
    if (next < 0 || next >= draft.length) return;
    const copy = [...draft];
    [copy[index], copy[next]] = [copy[next]!, copy[index]!];
    setDraft(copy.map((r, i) => ({ ...r, sortOrder: i })));
  }

  async function commit() {
    setBusy(true);
    try {
      const cleaned = draft
        .filter((r) => r.label.trim())
        .map((r, i) => ({ ...r, label: r.label.trim(), sortOrder: i }));
      await saveRows.mutateAsync({ week, rows: cleaned });
      onClose();
    } finally {
      setBusy(false);
    }
  }

  async function saveAsDefault() {
    const cleaned = draft.filter((r) => r.label.trim()).map((r, i) => ({ ...r, sortOrder: i }));
    if (cleaned.length === 0) return;
    await saveRows.mutateAsync({ week, rows: cleaned });
    await saveTemplate.mutateAsync({ name: 'My schedule', rows: cleaned });
    setSaved('Saved as your default schedule for new weeks.');
  }

  return (
    <Modal
      onClose={onClose}
      label="Schedule"
      className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-card bg-white p-5 shadow-lifted"
    >
      <h2 className="mb-1 text-lg">Schedule</h2>
      <p className="mb-4 text-xs text-stone">
        The subjects down the left of the table. Changes apply to this week. Tick{' '}
        <span className="text-walnut">no students</span> for blocks the class isn't with you —
        Specials, lunch, prep. They stay on the plan but grey out.
      </p>

      <div className="space-y-1.5">
        {draft.map((row, i) => (
          <div
            key={row.id}
            className={[
              'flex items-center gap-1.5 rounded-lg px-1 py-0.5',
              row.noStudents ? 'bg-linen/70' : '',
            ].join(' ')}
          >
            <div className="flex flex-col">
              <button
                onClick={() => move(i, -1)}
                disabled={i === 0}
                aria-label="Move up"
                className="text-[10px] leading-none text-stone hover:text-espresso disabled:opacity-30"
              >
                ▲
              </button>
              <button
                onClick={() => move(i, 1)}
                disabled={i === draft.length - 1}
                aria-label="Move down"
                className="text-[10px] leading-none text-stone hover:text-espresso disabled:opacity-30"
              >
                ▼
              </button>
            </div>

            <input
              className="field flex-1 py-1.5 text-sm"
              value={row.label}
              maxLength={80}
              placeholder="Subject"
              onChange={(e) => update(row.id, { label: e.target.value })}
              aria-label="Subject name"
            />
            <input
              className="field w-20 py-1.5 text-center text-xs"
              value={row.timeStart ?? ''}
              maxLength={12}
              placeholder="8:45"
              onChange={(e) => update(row.id, { timeStart: e.target.value || null })}
              aria-label="Start time"
            />
            <span className="text-xs text-stone">–</span>
            <input
              className="field w-20 py-1.5 text-center text-xs"
              value={row.timeEnd ?? ''}
              maxLength={12}
              placeholder="9:45"
              onChange={(e) => update(row.id, { timeEnd: e.target.value || null })}
              aria-label="End time"
            />
            <label
              className="flex shrink-0 cursor-pointer items-center gap-1 px-1 text-[10px] text-walnut"
              title="The students are elsewhere during this block"
            >
              <input
                type="checkbox"
                checked={row.noStudents}
                onChange={(e) => update(row.id, { noStudents: e.target.checked })}
                className="h-3 w-3 accent-clay"
              />
              no students
            </label>
            <button
              onClick={() => setDraft((d) => d.filter((r) => r.id !== row.id))}
              aria-label={`Remove ${row.label}`}
              className="px-1 text-stone hover:text-rust"
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={() =>
          setDraft((d) => [
            ...d,
            {
              id: newId(),
              label: '',
              timeStart: null,
              timeEnd: null,
              sortOrder: d.length,
              noStudents: false,
            },
          ])
        }
        className="btn-soft-accent mt-3"
      >
        + Add a subject
      </button>

      {saved && <p className="mt-3 text-xs text-olive">{saved}</p>}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
        <button
          onClick={() => void saveAsDefault()}
          className="text-xs text-walnut hover:underline"
        >
          Save as my default schedule
        </button>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button onClick={() => void commit()} className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
